import { ChangeDetectionStrategy, Component, computed, input, output, signal } from '@angular/core';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatExpansionModule } from '@angular/material/expansion';
import { ButtonComponent } from '@examdojo/core/button';
import { IconButtonComponent } from '@examdojo/core/icon-button';
import { IconComponent } from '@examdojo/core/icon';
import { MarkdownViewerComponent } from '@examdojo/core/ui/markdown-viewer';
import type { EChartsOption } from 'echarts';
import { BarChart, ScatterChart } from 'echarts/charts';
import { GridComponent } from 'echarts/components';
import * as echarts from 'echarts/core';
import { SVGRenderer } from 'echarts/renderers';
import { NgxEchartsDirective, provideEchartsCore } from 'ngx-echarts';
import { v4 as uuid } from 'uuid';

echarts.use([ScatterChart, BarChart, SVGRenderer, GridComponent]);

import type { DataInputConfig } from '../../../widgets/model';
import type { StatTestType } from './test-library-datasets.model';
import type { TestLibraryDataset } from './test-library-datasets.model';
import { TEST_LIBRARY_DATASETS } from './test-library-datasets.model';
import {
  CONTINGENCY_SIZE_OPTIONS,
  DECIMAL_PLACES_OPTIONS,
  DEFAULT_TEST_CONFIG,
  EFFECT_PRESETS,
  EFFECT_PRESET_LABELS,
  GOF_CATEGORY_OPTIONS,
  SAMPLE_SIZE_PRESETS,
  TEST_TYPE_TITLE_LABELS,
  getEffectLabel,
  getEffectMetricLabel,
  getEffectMetricValue,
  isChiSquared,
  needsContingencySize,
  needsGofCategories,
  needsGroupSizeToggle,
  needsHypothesizedMean,
} from './test-generator.model';
import { generateTestData } from './test-distributions.util';

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

@Component({
  selector: 'dojo-test-generator',
  standalone: true,
  imports: [
    MatButtonToggleModule,
    MatExpansionModule,
    ButtonComponent,
    IconButtonComponent,
    IconComponent,
    MarkdownViewerComponent,
    NgxEchartsDirective,
  ],
  providers: [provideEchartsCore({ echarts })],
  templateUrl: './test-generator.component.html',
  styleUrl: './test-generator.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TestGeneratorComponent {
  readonly activeTestType = input.required<StatTestType>();

  readonly initOpts = { renderer: 'svg' } as const;
  readonly generate = output<DataInputConfig>();
  readonly closePanel = output<void>();

  // ── Test type (from input) ───────────────────────────────────────────
  readonly testType = this.activeTestType;

  // ── Dynamic title ────────────────────────────────────────────────────
  readonly panelTitle = computed(() => `${TEST_TYPE_TITLE_LABELS[this.testType()]} Data Generator`);

  // ── Accordion state ────────────────────────────────────────────────────
  readonly libraryExpanded = signal(true);
  readonly generateExpanded = signal(false);

  // ── Library (filtered by selected test type) ──────────────────────────
  readonly filteredDatasets = computed(() => TEST_LIBRARY_DATASETS.filter((d) => d.testType === this.testType()));

  // ── Generate: Effect strength ──────────────────────────────────────────
  readonly effectStrength = signal(DEFAULT_TEST_CONFIG.effectStrength);
  readonly effectPresets = EFFECT_PRESETS;
  readonly effectPresetLabels = EFFECT_PRESET_LABELS;

  // ── Generate: Sample size ──────────────────────────────────────────────
  readonly sampleSize = signal(DEFAULT_TEST_CONFIG.sampleSize);
  readonly sampleSizePresets = SAMPLE_SIZE_PRESETS;

  // ── Generate: Test-specific ────────────────────────────────────────────
  readonly hypothesizedMean = signal(DEFAULT_TEST_CONFIG.hypothesizedMean);
  readonly equalGroupSizes = signal(DEFAULT_TEST_CONFIG.equalGroupSizes);
  readonly contingencyRows = signal(DEFAULT_TEST_CONFIG.contingencyRows);
  readonly contingencyCols = signal(DEFAULT_TEST_CONFIG.contingencyCols);
  readonly gofCategories = signal(DEFAULT_TEST_CONFIG.gofCategories);

  // ── Generate: Decimal places ───────────────────────────────────────────
  readonly decimalPlaces = signal(DEFAULT_TEST_CONFIG.decimalPlaces);
  readonly decimalPlacesOptions = DECIMAL_PLACES_OPTIONS;

  // ── Generate: Options ──────────────────────────────────────────────────
  readonly contingencySizeOptions = CONTINGENCY_SIZE_OPTIONS;
  readonly gofCategoryOptions = GOF_CATEGORY_OPTIONS;

  // ── Computed: conditional visibility ────────────────────────────────────
  readonly showHypothesizedMean = computed(() => needsHypothesizedMean(this.testType()));
  readonly showGroupSizeToggle = computed(() => needsGroupSizeToggle(this.testType()));
  readonly showContingencySize = computed(() => needsContingencySize(this.testType()));
  readonly showGofCategories = computed(() => needsGofCategories(this.testType()));

  // ── Computed: labels & display ─────────────────────────────────────────
  readonly effectLabel = computed(() => getEffectLabel(this.testType()));

  readonly effectMetricLabel = computed(() => getEffectMetricLabel(this.testType()));
  readonly effectMetricValue = computed(() => getEffectMetricValue(this.testType(), this.effectStrength()));

  readonly sampleSizeLabel = computed(() => (isChiSquared(this.testType()) ? 'TOTAL COUNT' : 'SAMPLE SIZE'));

  readonly strengthSliderProgress = computed(() => `${Math.round(this.effectStrength() * 100)}%`);

  readonly sampleSliderProgress = computed(() => {
    const val = this.sampleSize();
    return `${Math.round(((val - 5) / 195) * 100)}%`;
  });

  readonly generateButtonLabel = computed(() => {
    const type = this.testType();
    const n = this.sampleSize();
    if (isChiSquared(type)) {
      return `Generate Table (N = ${n})`;
    }
    return `Generate Dataset (n = ${n})`;
  });

  // ── Computed: preview ──────────────────────────────────────────────────

  readonly previewData = computed(() => {
    const type = this.testType();
    const previewN = isChiSquared(type) ? 60 : 20;
    return generateTestData({
      testType: type,
      effectStrength: this.effectStrength(),
      sampleSize: previewN,
      decimalPlaces: 1,
      hypothesizedMean: this.hypothesizedMean(),
      equalGroupSizes: this.equalGroupSizes(),
      contingencyRows: this.contingencyRows(),
      contingencyCols: this.contingencyCols(),
      gofCategories: this.gofCategories(),
      seed: `preview-${type}-${this.effectStrength()}-${this.hypothesizedMean()}-${this.contingencyRows()}-${this.contingencyCols()}-${this.gofCategories()}`,
    });
  });

  readonly previewChartOptions = computed<EChartsOption>(() => {
    const data = this.previewData();
    const type = this.testType();

    switch (type) {
      case 'one-sample-t':
        return this.buildOneSamplePreview(data);
      case 'two-sample-t':
      case 'paired-t':
        return this.buildTwoGroupPreview(data);
      case 'chi-squared-independence':
        return this.buildContingencyPreview(data);
      case 'chi-squared-gof':
        return this.buildGofPreview(data);
      case 'sign-test':
        return this.buildSignTestPreview(data);
    }
  });

  // ── Preview chart grid ─────────────────────────────────────────────────

  private readonly CHART_GRID = { left: 8, right: 8, top: 8, bottom: 8, containLabel: false };

  // ── Library actions ────────────────────────────────────────────────────

  getDatasetSize(dataset: TestLibraryDataset): number {
    return Math.max(...dataset.columns.map((c) => c.values.length));
  }

  onLoadDataset(dataset: TestLibraryDataset): void {
    const colIds = dataset.columns.map(() => uuid());
    const maxLen = Math.max(...dataset.columns.map((c) => c.values.length));

    const config: DataInputConfig = {
      columns: dataset.columns.map((col, i) => ({
        id: colIds[i],
        header: col.header,
        colType: col.colType,
      })),
      rows: Array.from({ length: maxLen }, (_, rowIdx) => {
        const row: Record<string, number | string | null> = {};
        dataset.columns.forEach((col, colIdx) => {
          row[colIds[colIdx]] = rowIdx < col.values.length ? col.values[rowIdx] : null;
        });
        return row;
      }),
      rowCount: maxLen,
      editableHeaders: true,
      allowAddColumns: isChiSquared(dataset.testType),
      allowAddRows: true,
    };

    this.generate.emit(config);
  }

  // ── Generate actions ───────────────────────────────────────────────────

  setEffectStrength(value: number): void {
    this.effectStrength.set(value);
  }

  onEffectSlider(event: Event): void {
    this.effectStrength.set(Number((event.target as HTMLInputElement).value));
  }

  onSampleSizeSlider(event: Event): void {
    this.sampleSize.set(Number((event.target as HTMLInputElement).value));
  }

  setSampleSize(n: number): void {
    this.sampleSize.set(n);
  }

  onHypothesizedMeanInput(event: Event): void {
    const val = Number((event.target as HTMLInputElement).value);
    if (!isNaN(val)) {
      this.hypothesizedMean.set(val);
    }
  }

  setEqualGroupSizes(equal: boolean): void {
    this.equalGroupSizes.set(equal);
  }

  setContingencySize(rows: number, cols: number): void {
    this.contingencyRows.set(rows);
    this.contingencyCols.set(cols);
  }

  setGofCategories(k: number): void {
    this.gofCategories.set(k);
  }

  setDecimalPlaces(dp: number): void {
    this.decimalPlaces.set(dp);
  }

  onGenerate(): void {
    const data = generateTestData({
      testType: this.testType(),
      effectStrength: this.effectStrength(),
      sampleSize: this.sampleSize(),
      decimalPlaces: this.decimalPlaces(),
      hypothesizedMean: this.hypothesizedMean(),
      equalGroupSizes: this.equalGroupSizes(),
      contingencyRows: this.contingencyRows(),
      contingencyCols: this.contingencyCols(),
      gofCategories: this.gofCategories(),
      seed: '',
    });

    const colIds = data.columns.map(() => uuid());
    const maxLen = Math.max(...data.columns.map((c) => c.values.length));

    const config: DataInputConfig = {
      columns: data.columns.map((col, i) => ({
        id: colIds[i],
        header: col.header,
        colType: col.colType,
      })),
      rows: Array.from({ length: maxLen }, (_, rowIdx) => {
        const row: Record<string, number | string | null> = {};
        data.columns.forEach((col, colIdx) => {
          row[colIds[colIdx]] = rowIdx < col.values.length ? col.values[rowIdx] : null;
        });
        return row;
      }),
      rowCount: maxLen,
      editableHeaders: true,
      allowAddColumns: isChiSquared(this.testType()),
      allowAddRows: true,
    };

    this.generate.emit(config);
  }

  // ── Preview chart builders ─────────────────────────────────────────────

  private buildOneSamplePreview(data: ReturnType<typeof generateTestData>): EChartsOption {
    const values = data.columns[0].values as number[];
    const jitter = (i: number) => 0.5 + Math.sin(i * 137.508) * 0.25;

    return {
      grid: this.CHART_GRID,
      xAxis: { type: 'value' as const, show: false, min: 0, max: 1 },
      yAxis: { type: 'value' as const, show: false },
      series: [
        {
          type: 'scatter' as const,
          data: values.map((v, i) => [jitter(i), v]),
          symbolSize: 6,
          itemStyle: { color: '#0ea5e9', opacity: 0.85 },
        },
      ],
      animation: false,
    };
  }

  private buildTwoGroupPreview(data: ReturnType<typeof generateTestData>): EChartsOption {
    const col1 = (data.columns[0].values as Array<number | null>).filter((v): v is number => v !== null);
    const col2 = (data.columns[1].values as Array<number | null>).filter((v): v is number => v !== null);
    const jitter = (i: number, offset: number) => offset + Math.sin(i * 137.508) * 0.15;

    return {
      grid: this.CHART_GRID,
      xAxis: { type: 'value' as const, show: false, min: -0.3, max: 1.3 },
      yAxis: { type: 'value' as const, show: false },
      series: [
        {
          type: 'scatter' as const,
          data: col1.map((v, i) => [jitter(i, 0.2), v]),
          symbolSize: 6,
          itemStyle: { color: '#0ea5e9', opacity: 0.85 },
        },
        {
          type: 'scatter' as const,
          data: col2.map((v, i) => [jitter(i, 0.8), v]),
          symbolSize: 6,
          itemStyle: { color: '#f97316', opacity: 0.85 },
        },
      ],
      animation: false,
    };
  }

  private buildContingencyPreview(data: ReturnType<typeof generateTestData>): EChartsOption {
    const numericCols = data.columns.filter((c) => c.colType === 'number');
    const categories = numericCols.map((c) => c.header);
    const nRows = data.columns[0].values.length;
    const colors = ['#0ea5e9', '#f97316', '#22c55e', '#a855f7'];

    return {
      grid: this.CHART_GRID,
      xAxis: { type: 'category' as const, data: categories, show: false },
      yAxis: { type: 'value' as const, show: false },
      series: Array.from({ length: nRows }, (_, i) => ({
        type: 'bar' as const,
        data: numericCols.map((c) => c.values[i] as number),
        itemStyle: { color: colors[i % colors.length] },
      })),
      animation: false,
    };
  }

  private buildGofPreview(data: ReturnType<typeof generateTestData>): EChartsOption {
    const categories = data.columns[0].values as string[];
    const observed = data.columns[1].values as number[];

    return {
      grid: this.CHART_GRID,
      xAxis: { type: 'category' as const, data: categories, show: false },
      yAxis: { type: 'value' as const, show: false },
      series: [
        {
          type: 'bar' as const,
          data: observed,
          itemStyle: { color: '#0ea5e9' },
        },
      ],
      animation: false,
    };
  }

  private buildSignTestPreview(data: ReturnType<typeof generateTestData>): EChartsOption {
    const col1 = data.columns[0].values as number[];
    const col2 = data.columns[1].values as number[];
    let pos = 0;
    let neg = 0;
    for (let i = 0; i < col1.length; i++) {
      if (col2[i] > col1[i]) {
        pos++;
      } else if (col2[i] < col1[i]) {
        neg++;
      }
    }

    return {
      grid: this.CHART_GRID,
      xAxis: { type: 'category' as const, data: ['+', '\u2212'], show: false },
      yAxis: { type: 'value' as const, show: false },
      series: [
        {
          type: 'bar' as const,
          data: [
            { value: pos, itemStyle: { color: '#22c55e' } },
            { value: neg, itemStyle: { color: '#ef4444' } },
          ],
        },
      ],
      animation: false,
    };
  }
}
